// src/pages/OrderDetailsPage.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useOrders } from "../../hooks/useOrders";

export default function OrderDetailsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { order, loading, fetchOrderById, updateOrderStatus } = useOrders();

    useEffect(() => {
        if (id) fetchOrderById(id);
    }, [id, fetchOrderById]);

    const handleStatusChange = async (selectedOption) => {
        if (!selectedOption) return;
        
        const newStatus = selectedOption.value;
        if (window.confirm(`Are you sure you want to change the status to ${newStatus}?`)) {
            await updateOrderStatus(id, newStatus);
        }
    };

    if (loading || !order) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center min-vh-100 bg-light">
                <div className="spinner-border text-primary border-3 mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                </div>
                <h5 className="text-muted fw-semibold">Loading order details...</h5>
            </div>
        );
    }

    const statusOptions = [
        { value: "Placed", label: "Placed" },
        { value: "Processing", label: "Processing" },
        { value: "Shipped", label: "Shipped" },
        { value: "Delivered", label: "Delivered" },
        { value: "Cancelled", label: "Cancelled" }
    ];

    const selectStyles = {
        control: (base) => {
            let borderColor = '#0d6efd'; 
            if (order.orderStatus === 'Delivered') borderColor = '#198754'; 
            if (order.orderStatus === 'Cancelled') borderColor = '#dc3545'; 

            return {
                ...base,
                width: '160px',
                borderColor: borderColor,
                boxShadow: 'none',
                cursor: 'pointer',
                '&:hover': {
                    borderColor: borderColor
                }
            };
        },
        singleValue: (base) => {
            let color = '#0d6efd'; 
            if (order.orderStatus === 'Delivered') color = '#198754';
            if (order.orderStatus === 'Cancelled') color = '#dc3545';

            return {
                ...base,
                color: color,
                fontWeight: 'bold'
            };
        },
        option: (baseStyles, state) => ({
            ...baseStyles,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
            color: state.isSelected ? 'white' : 'black',
            cursor: 'pointer'
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div>
                    <button className="btn btn-sm btn-white border shadow-sm mb-3 fw-semibold text-dark" onClick={() => navigate("/ecommerce/orders")}>
                        ← Back to Orders
                    </button>
                    <h3 className="mb-1 fw-bolder text-dark d-flex align-items-center gap-2">
                        Order <span className="text-primary">#{order._id.substring(order._id.length - 8).toUpperCase()}</span>
                    </h3>
                    <div className="text-muted small fw-medium">
                        Placed on {new Date(order.createdAt).toLocaleString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </div>
                </div>
                
                <div className="card shadow-sm border-0 bg-white px-3 py-2 rounded-3">
                    <div className="d-flex align-items-center gap-3">
                        <label className="fw-bold small text-muted text-uppercase mb-0">Order Status:</label>
                        
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(opt => opt.value === (order.orderStatus || 'Placed'))}
                            onChange={handleStatusChange}
                            styles={selectStyles}
                            isSearchable={false}
                            menuPortalTarget={document.body}
                            menuPosition={'fixed'}
                        />
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-8">
                    <div className="card shadow-sm border-0 mb-4 rounded-3 overflow-hidden">
                        <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-2">
                            <span className="fs-5">🛍️</span>
                            <h6 className="mb-0 fw-bold text text-dark">Order Items ({order.orderItems?.length || 0})</h6>
                        </div>
                        
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="px-4 py-3 small text-muted text-uppercase fw-bold">Product Details</th>
                                            <th className="py-3 small text-muted text-uppercase text-center fw-bold text">Price</th>
                                            <th className="py-3 small text-muted text-uppercase text-center fw-bold">Qty</th>
                                            <th className="px-4 py-3 small text-muted text-uppercase text-end fw-bold">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.orderItems?.map((item) => (
                                            <tr key={item._id} className="transition-all">
                                                <td className="px-4 py-3 d-flex align-items-center gap-3">
                                                    <div className="bg-light border rounded d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '60px', height: '60px', overflow: 'hidden' }}>
                                                        {item.product?.images?.[0] ? (
                                                            <img src={item.product.images[0]} alt={item.product?.title || "Product"} className="w-100 h-100 object-fit-cover" />
                                                        ) : (
                                                            <span className="text-muted fs-4">📦</span>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fw-bold text-dark">{item.product?.title || "Product Title Unavailable"}</h6>
                                                        <span className="badge bg-secondary bg-opacity-10 text-secondary border smallfw-bold text text-dark">
                                                            ID: {item.product?._id || item.product}
                                                        </span>
                                                        {item.selectedVariant && (
                                                            <div className="text-muted small mt-1">
                                                                Variant: {item.selectedVariant.attributes?.map(a => a.name).join(', ')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="py-3 text-center text-muted fw-medium">
                                                    ₹{item.price?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </td>
                                                <td className="py-3 text-center">
                                                    <span className="badge bg-light text-dark border px-3 py-2 rounded-pill fw-bold">
                                                        x{item.quantity}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-end fw-bold text-dark fs-6">
                                                    ₹{(item.price * item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="card-footer bg-white border-top p-4 d-flex justify-content-end">
                            <div style={{ width: '300px' }}>
                                <div className="d-flex justify-content-between mb-2 text-muted fw-medium">
                                    <span>Subtotal</span>
                                    <span>₹{order.itemsPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-2 text-muted fw-medium">
                                    <span>Shipping</span>
                                    <span>₹{order.shippingPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="d-flex justify-content-between mb-3 text-muted fw-medium">
                                    <span>Tax</span>
                                    <span>₹{order.taxPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                </div>
                                <div className="d-flex justify-content-between pt-3 border-top mt-2">
                                    <span className="fw-bolder text-dark fs-5">Total Amount</span>
                                    <span className="fw-bolder text-primary fs-4">
                                        ₹{order.totalPrice?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4 d-flex flex-column gap-4">
                    
                    <div className="card shadow-sm border-0 rounded-3"> 
                        <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-2">
                            <span className="fs-5">👤</span>
                            <h6 className="mb-0 fw-bold text-secondary">Customer Info</h6>
                        </div>
                        <div className="card-body p-4">
                            <h6 className="fw-bold mb-2 text-dark fs-5">
                                {order.shippingAddress?.fullName || order.userId?.name || "Guest Customer"}
                            </h6>
                            <div className="text-muted small d-flex flex-column gap-2">
                                {order.userId?.email && (
                                    <span className="d-flex align-items-center gap-2">
                                        📧 {order.userId.email}
                                    </span>
                                )}
                                <span className="d-flex align-items-center gap-2 mt-1">
                                    <span className="badge bg-light text-dark border">
                                        {order.userId ? "Registered User" : "Guest Checkout"}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 rounded-3">
                        <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-2">
                            <span className="fs-5">📍</span>
                            <h6 className="mb-0 fw-bold text-secondary">Shipping Address</h6>
                        </div>
                        <div className="card-body p-4">
                            <div className="bg-light p-3 rounded-3 border border-light-subtle text-dark fw-medium">
                                <p className="mb-1">{order.shippingAddress?.address}</p>
                                <p className="mb-1">{order.shippingAddress?.city} - {order.shippingAddress?.postalCode}</p>
                                <p className="mb-0 text-uppercase fw-bold text-muted mt-2">{order.shippingAddress?.country}</p>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card shadow-sm border-0 rounded-3">
                        <div className="card-header bg-white border-bottom py-3 d-flex align-items-center gap-2">
                            <span className="fs-5">💳</span>
                            <h6 className="mb-0 fw-bold text-secondary">Payment Details</h6>
                        </div>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-muted fw-medium small">Payment Method</span>
                                <span className="fw-bold text-dark">{order.paymentMethod}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-muted fw-medium small">Payment Status</span>
                                <span className={`badge px-3 py-2 rounded-pill shadow-sm ${order.isPaid ? 'bg-success' : 'bg-warning text-dark'}`}>
                                    {order.isPaid ? 'Paid' : 'Pending / Unpaid'}
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}